import { useState, Fragment, useCallback, useEffect } from 'react';
import {
  Row,
  Col,
  Button,
  Table,
  message,
  Spin,
  Input,
  Form,
  Select,
  Modal,
} from 'antd';
import moment from 'moment';
import { handlers } from '../../../helpers/helpers';
import { getAsset } from '../../../services/general';
import { evaluateDocument, getDocumentRejectionReasons, finalizePrescriptionDocumentsEvaluation } from '../../../services/reservation';

const { Option } = Select;

const AvailableDocuments = ({
  t,
  loading,
  setLoading,
  isFinish,
  showModal,
  countryCode,
  countrySettings,
  docsRequired,
  allDocsStatus,
  reevaluationData,
  prescriptionItems,
  medicalConsultationId,
  fetchReevaluationData,
  renderReviewConfirmation,
}) => {

  const [form] = Form.useForm();
  const [expandedRowKey, setExpandedRowKey] = useState([]);
  const [pdfsUrlByKey, setPdfsUrlByKey] = useState({});
  const [rejectionList, setRejectionList] = useState([]);
  const [selectedRejectionReasons, setSelectedRejectionReasons] = useState([]);
  const [modalStatusVisible, setModalStatusVisible] = useState({ open: false, status: null });


  const fetchRejectionReasons = useCallback(async () => {
    try {
      setLoading(loading => { return { ...loading, rejectionList: true } });

      const response = await getDocumentRejectionReasons();

      if (response.length) {
        setRejectionList([...response]);
      }
    } catch (error) {
      console.error('Error en fetchRejectionReasons: ', error);
      message.error({
        content: 'Error al obtener listado de motivos de rechazo.',
        style: {
          marginTop: '40vh',
        },
      });
    } finally {
      setLoading(loading => { return { ...loading, rejectionList: false } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    fetchRejectionReasons();
  }, [fetchRejectionReasons]);

  const columnsPE = [
    {
      key: 1,
      dataIndex: 'name',
      title: 'Nombre',
      render: (text) => {
        return <p className="mb-0">{text}</p>
      }
    },
    {
      key: 2,
      dataIndex: 'exam_document_id',
      title: '',
      width: 10,
      render: (text) => {
        return (
          <Button
            loading={loading.downloadPdf}
            onClick={() => downloadPDF(text, null, true)}
            className="cmt-button secondary outline pl-10 pr-10"
            type="primary"
            size="small"
            style={{
              fontSize: 14,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            Ver documento
          </Button>
        )
      }
    }
  ];

  const handleToggleRow = (record) => {
    const currentKey = record.key;
    const currentExamDocId = record.exam_document_id;
    const isRowExpanded = expandedRowKey.includes(currentKey);

    if (!isRowExpanded && currentExamDocId) {
      setSelectedRejectionReasons([]);
      downloadPDF(currentExamDocId, currentKey);
    }

    setExpandedRowKey(prev =>
      prev.includes(currentKey) ? prev.filter(elem => elem !== currentKey) : [currentKey]
    );
  };

  const handleStateColor = (state) => {
    switch (state) {
      case 'pending_revision':
        return {
          color: '#EE8D3A',
          state: 'Pendiente',
        };
      case 'rejected':
        return {
          color: '#D02A2A',
          state: 'No aceptado',
        };
      case 'accepted':
        return {
          color: '#2F9933',
          state: 'Aceptado'
        };
      default:
        return {
          color: '#989898',
          state: 'Sin documentos'
        };
    }
  };

  const columnsCL = [
    {
      key: 1,
      dataIndex: 'name',
      title: 'Nombre documento requerido',
      render: (text) => {
        return <p className="mb-0">{text}</p>
      }
    },
    {
      key: 2,
      dataIndex: 'last_update',
      width: '30%',
      title: <p className='mb-0'><strong>Historial de carga <br /> Fecha y hora</strong></p>,
      render: (text) => {
        return <p className="mb-0">{text ? `${moment(text).format('DD-MM-YYYY HH:mm:ss')} hs` : '-'}</p>
      }
    },
    {
      key: 3,
      dataIndex: 'state',
      title: 'Estado',
      width: '15%',
      render: (text) => {
        return text && <p className="mb-0" style={{ color: handleStateColor(text).color }}>{handleStateColor(text).state}</p>
      },
    },
    {
      key: 'action',
      title: '',
      width: '20%',
      render: (_, record) => {
        const isOpen = expandedRowKey.includes(record.key);

        return (
          <Button
            className={(record.state !== 'pending_upload' || !loading.confirmRevision) ? 'cmt-button secondary outline' : 'cmt-button disabled outline'}
            type="primary"
            disabled={record.state === 'pending_upload' || loading.confirmRevision}
            size='small'
            onClick={(e) => {
              e.stopPropagation();
              handleToggleRow(record);
            }}
          >
            Ver documento
            <span className={!isOpen ? 'icon-system-chevron-down ml-10' : 'icon-system-chevron-up ml-10'} />
          </Button>
        );
      },
    },
  ];

  const downloadPDF = async (examDocumentId, currentKey, downloadItem = false) => {

    try {
      setLoading(loading => { return { ...loading, downloadPdf: true } });

      const response = await getAsset({
        id: examDocumentId,
        type: 'ExamsDocument',
        field: 'document'
      });

      if (downloadItem && response['document_url']) {

        handlers.urlDownload(response['document_url'], message);

      } else if (!downloadItem && currentKey && response['document_url']) {

        setPdfsUrlByKey(prevKeys => ({ ...prevKeys, [currentKey]: response['document_url'] }));

        const splitCurrentKey = currentKey.split('@');
        const currentPrescriptionIndex = prescriptionItems.findIndex(item => item.name === splitCurrentKey[0]);
        const currentDocIndex = parseInt(splitCurrentKey[1]);
        const currentRejectKeys = prescriptionItems[currentPrescriptionIndex]?.documents[currentDocIndex].reject_reason_keys;
        const currentMedicalDocId = prescriptionItems[currentPrescriptionIndex]?.documents[currentDocIndex].medical_prescription_document_id;

        if (Array.isArray(currentRejectKeys) && currentRejectKeys.length > 0) {
          setSelectedRejectionReasons([...currentRejectKeys]);

          form.setFieldsValue({
            rejectionKeys: currentRejectKeys,
          });
        } else {
          form.setFieldsValue({
            rejectionKeys: undefined,
          });
        }

        form.setFieldsValue({
          medicalPrescriptionDocumentId: currentMedicalDocId,
        });

      } else {
        message.error({
          content: 'Ha ocurrido un error al descargar el documento. Inténtalo de nuevo.',
          style: {
            marginTop: '35vh'
          }
        });
      }
    } catch (error) {
      console.error(`Error interno: ${error}`);
      message.error({
        content: `Error interno al descargar Informe.`,
        style: {
          marginTop: '35vh'
        }
      });
    } finally {
      setLoading(loading => { return { ...loading, downloadPdf: false } });
    }
  };

  const handleSelectedRejection = (event) => {
    const inputValues = event;

    setSelectedRejectionReasons(() => [...inputValues]);
  };

  const confirmDocumentsRevision = async () => {
    try {
      setLoading(loading => ({ ...loading, confirmRevision: true }));

      const response = await finalizePrescriptionDocumentsEvaluation({ medicalConsultationDetailId: medicalConsultationId });

      if (response?.status === 200) {
        message.success({
          content: 'Confirmación de documentos revisados exitosamente',
          style: {
            marginTop: '45vh',
          },
        });
        fetchReevaluationData({ medicalConsultationId });
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Error en confirmRevision: ', error);
      message.error({
        content: 'Error al confirmar revisión de documentos.',
        style: {
          marginTop: '45vh',
        },
      });
    } finally {
      setLoading(loading => ({ ...loading, confirmRevision: false }));
    }
  };


  const onFinish = async (values) => {
    setLoading(loading => ({ ...loading, documentApproval: true }));

    try {
      let params = {
        id: values.medicalPrescriptionDocumentId,
      };

      if (selectedRejectionReasons.length === 0) {
        params = {
          ...params,
          state: 'accepted',
        };
      } else {
        const rejectReasonString = rejectionList.filter((item) => values.rejectionKeys.includes(item.key)).map((elem) => elem.name).join('\n');

        params = {
          ...params,
          state: 'rejected',
          rejectReason: rejectReasonString,
          rejectReasonKeys: values.rejectionKeys,
        };
      }

      const response = await evaluateDocument(params);

      if (response?.id) {
        setModalStatusVisible({ open: true, status: params.state });
      }
    } catch (error) {
      console.error('Error al evaluar documento: ', error);
      message.error({
        content: 'Error al evaluar documento',
        style: {
          marginTop: '45vh',
        },
      });
    } finally {
      setLoading(loading => ({ ...loading, documentApproval: false }));
    }
  };


  return (
    <Row className="mt-40 mb-40 available-documents-container">
      <Col>
        <h2 className="mb-0">{isFinish && !isFinish.is_finish && '2. '}{t('reservations.reevaluationSettings.availableDocuments')}</h2>
      </Col>
      {
        countrySettings.reevaluations.renderNewAvailableDocs[countryCode] &&
        <Col lg={24} xs={24} className='mt-10 mb-20'>
          <Row gutter={[16, 20]} align='middle'>
            <Col className="status-wrapper"><div className='dot pending' /> <p className="mb-0">Pendiente de revisión</p></Col>
            <Col className="status-wrapper"><div className='dot error' /> <p className="mb-0">Documento no aceptado</p></Col>
            <Col className="status-wrapper"><div className='dot success' /> <p className="mb-0">Documento aceptado</p></Col>
            <Col className="status-wrapper"><div className='dot empty' /> <p className="mb-0">Sin documentos</p></Col>
          </Row>
        </Col>
      }
      {
        prescriptionItems.map((item, index) => {
          if (countrySettings.reevaluations.renderNewAvailableDocs[countryCode]) {

            const parentKeyName = item.name;
            const documentsWithKey = item.documents.map((data, _index) => ({ key: `${parentKeyName}@${_index}@${index}`, ...data }));

            return (
              <Fragment key={index}>
                <Col lg={24} xs={24} className='mt-20'>
                  <p className="mb-0"><strong>Prescripción médica:</strong>&nbsp;&nbsp;{item.name}</p>
                </Col>
                <Col lg={24} xs={24} key={index}>
                  <Table
                    rowKey="key"
                    className="cmt-table"
                    columns={columnsCL}
                    dataSource={documentsWithKey}
                    pagination={false}
                    locale={{ emptyText: 'No hay documentos para mostrar' }}
                    tableLayout="fixed"
                    expandable={{
                      expandIcon: () => null,
                      expandRowByClick: false,
                      expandedRowKeys: expandedRowKey,
                      onExpandedRowsChange: setExpandedRowKey,
                      expandedRowRender: (record) => {
                        const url = pdfsUrlByKey[record.key];

                        return (
                          <Row justify='center' className='iframe-pdf-container'>
                            <Col xl={20} xs={22}>
                              <Spin className='cmt-spin' spinning={loading.downloadPdf}>
                                <Row>
                                  <Col lg={24} xs={24} style={{ position: 'relative', width: '100%', height: '65vh' }}>
                                    <iframe
                                      src={`${url}#toolbar=1`}
                                      title={`doc-${record.key}`}
                                      loading="lazy"
                                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                                      allow="fullscreen"
                                    />
                                  </Col>
                                  <Col lg={24} xs={24} className='mt-30'>
                                    <p className='text-center'><strong>Si el documento coincide con lo solicitado en la prescripción médica, presione Aceptar. <br /> De lo contrario presione Rechazar para que el paciente vuelva a cargar el documento correspondiente.</strong></p>
                                    <p className='text-center important-text'><strong>Importante:</strong> El paciente y la Empresa serán informados una vez que se haya aceptado o rechazado el documento.</p>
                                  </Col>
                                  <Col lg={24} xs={24}>
                                    <Spin className='cmt-spin' spinning={loading.documentApproval}>
                                      <Form
                                        form={form}
                                        size='large'
                                        layout='vertical'
                                        onFinish={onFinish}
                                        className='cmt-form'
                                      >
                                        <Row justify='center' gutter={[23, 30]}>
                                          <Form.Item name='medicalPrescriptionDocumentId' hidden>
                                            <Input type='hidden' />
                                          </Form.Item>
                                          <Col>
                                            <Button
                                              className={selectedRejectionReasons.length > 0 ? 'cmt-button disabled' : 'cmt-button secondary'}
                                              type='primary'
                                              size='large'
                                              disabled={selectedRejectionReasons.length > 0}
                                              onClick={() => form.submit()}
                                            >
                                              Aceptar documento
                                            </Button>
                                          </Col>
                                          <Col lg={24} xs={24}>
                                            <Row align='top' gutter={[11, 20]}>
                                              <Col lg={17} xs={24}>
                                                <Spin className='cmt-spin' spinning={loading.rejectionList}>
                                                  {
                                                    rejectionList.length > 0 &&
                                                    <Form.Item name='rejectionKeys' label='Lista de motivos de rechazo' className='mb-0'>
                                                      <Select
                                                        mode='multiple'
                                                        className='cmt-select'
                                                        placeholder='Seleccione motivos de rechazo'
                                                        onChange={(event) => handleSelectedRejection(event)}
                                                      >
                                                        {
                                                          rejectionList.map((item, index) => (
                                                            <Option value={item.key} key={index}>
                                                              {item.name}
                                                            </Option>
                                                          ))
                                                        }
                                                      </Select>
                                                    </Form.Item>
                                                  }
                                                </Spin>
                                              </Col>
                                              <Col lg={7} xs={24} className='mt-lg-40'>
                                                <Button
                                                  className={selectedRejectionReasons.length > 0 ? 'cmt-button secondary outline' : 'cmt-button disabled outline'}
                                                  type='primary'
                                                  size='large'
                                                  disabled={selectedRejectionReasons.length === 0}
                                                  onClick={() => form.submit()}
                                                >
                                                  Rechazar documento
                                                </Button>
                                              </Col>
                                            </Row>
                                          </Col>
                                        </Row>
                                      </Form>
                                    </Spin>
                                  </Col>
                                </Row>
                              </Spin>
                            </Col>
                          </Row>
                        );
                      },
                    }}
                  />
                  <Row justify="end" className="mt-15">
                    <Col xxl={5} xl={6} lg={7} xs={24}>
                      <Button
                        onClick={() => showModal(index)}
                        className="cmt-button secondary"
                        type="primary"
                        size="middle"
                        block
                      >
                        Administrar documentos
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Fragment>
            )
          } else {
            return (
              <Col className="mt-40" lg={24} xs={24} key={index}>
                <p className="mb-0"><b>{item.name}</b></p>
                <Table
                  className="cmt-table"
                  columns={columnsPE}
                  dataSource={item.documents.filter(doc => doc.exam_document_id !== null)}
                  pagination={false}
                  locale={{ emptyText: 'No hay documentos para mostrar' }}
                />
                <Row justify="end" className="mt-15">
                  <Col lg={7} xs={24}>
                    <Button
                      onClick={() => showModal(index)}
                      className="cmt-button secondary"
                      type="primary"
                      size="middle"
                      block
                    >
                      Administrar documentos
                    </Button>
                  </Col>
                </Row>
              </Col>
            )
          }
        })
      }

      {
        (renderReviewConfirmation && docsRequired) &&
        <Col lg={24} xs={24} className='mt-30'>
          <Row className='docs-warning-wrapper'>
            <Col className='mr-10'>
              <span className='icon-alert-triangle-bold' />
            </Col>
            <Col className='text-container'>
              <p className='mb-0'><strong>Importante:</strong></p>
              <p className='mb-0'>Antes de iniciar la reevaluación, <strong>confirme la revisión completa de los documentos requeridos</strong>. Esta validación garantiza el envío correcto de las notificaciones de estado, vía correo electrónico, tanto al trabajador como a la empresa. <br /> <br /> <strong>Al recargar documentos No aceptados</strong>, se reemplazarán los archivos anteriores por los más recientes.</p>
            </Col>
          </Row>
          <Row justify='center' className='mt-40'>
            <Col lg={4} xs={24}>
              <Button
                size='large'
                onClick={confirmDocumentsRevision}
                loading={loading.confirmRevision}
                disabled={!allDocsStatus.reviewed || loading.confirmRevision || reevaluationData?.documents_evaluated_notified_at}
                className={(allDocsStatus.reviewed && !loading.confirmRevision && !reevaluationData?.documents_evaluated_notified_at) ? 'cmt-button primary outline' : 'cmt-button disabled outline'}
              >
                Confirmar revisión
              </Button>
            </Col>
          </Row>
        </Col>
      }

      <Modal
        open={modalStatusVisible.open}
        footer={false}
        className='modal-status-reevaluation'
        closeIcon={<span className="icon-system-close" />}
        centered
        closable
        onCancel={
          () => {
            setModalStatusVisible({ open: false, status: null })
            fetchReevaluationData({ medicalConsultationId })
          }
        }
        responsive
        width={690}
      >
        <Row justify='center' className='pt-70 pl-30 pr-30 pb-40'>
          {
            modalStatusVisible.status === 'rejected' ?
              <Col className='rejected-content'>
                <h1>Documentos Rechazados</h1>
                <p>Los documentos quedarán en estado <strong>No Aceptado</strong>.<br /> Se les notificará por correo electrónico al paciente y la empresa informando el motivo del rechazo de sus documentos.</p>
              </Col>
              :
              <Col className='accepted-content'>
                <h1>Documentos aceptados con éxito</h1>
                <p>Los documentos quedarán en estado <strong>Aceptado</strong>.<br /> Se les notificará por correo electrónico al paciente y la empresa, para continuar con el proceso de reevaluación.</p>
              </Col>
          }
        </Row>
      </Modal>
    </Row>
  );
};

export default AvailableDocuments;